import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  IconFileText,
  IconLoader2,
  IconPlaylist,
  IconX,
} from "@tabler/icons-react";
import { iosGenerateHaptic } from "../../../tools/ios";
import { HapticEffect } from "../../../interfaces";
import type {
  ChatReferenceKind,
  ChatReferences as ChatReferencesState,
  SettledChatReference,
} from "../../../domains/gpt/hooks/useChatReferences";
import type { ChatMentionResult } from "../../../domains/gpt/hooks/useChatMention";

// The composer's reference chips + the transcript list. Purely presentational —
// `useChatReferences` owns the list, the add/remove and the errors. The twin of
// `ChatAttachments`' chips/list, for referenced Juisci content.

/** The icon that tells the two kinds apart, everywhere they are rendered. */
function ReferenceIcon({
  kind,
}: {
  kind: ChatReferenceKind;
}): React.ReactElement {
  switch (kind) {
    case "playlist":
      return <IconPlaylist className="size-4 shrink-0 text-ds-mint-800" />;
    case "article":
      return <IconFileText className="size-4 shrink-0 text-ds-mint-800" />;
    default:
      throw new Error(kind satisfies never);
  }
}

/**
 * Where a settled reference points in the app, or null when it points nowhere.
 * A playlist is routed by id, an article by SLUG (`/post/:slug` — the id 404s),
 * and the slug is null once the article is gone: an unlinkable chip.
 */
function referenceHref(reference: SettledChatReference): string | null {
  switch (reference.kind) {
    case "playlist":
      return `/profile/playlist/${reference.targetId}`;
    case "article":
      return reference.slug === null ? null : `/post/${reference.slug}`;
    default:
      throw new Error(reference.kind satisfies never);
  }
}

/** The referenced-sources row above the input. Renders nothing when empty. */
export function ChatReferenceChips({
  references,
  canRemove,
}: {
  references: ChatReferencesState;
  // False while a turn is streaming on the landing page: the references are shown
  // (the turn is grounding on them) but not editable.
  canRemove: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation();
  if (references.references.length === 0) return null;

  return (
    <div className="mb-2 flex flex-wrap gap-2">
      {references.references.map((item) => (
        <div
          key={
            item.status === "referenced"
              ? item.refId
              : `adding-${String(item.addId)}`
          }
          className="flex max-w-full items-center gap-1.5 rounded-full bg-white px-3 py-1.5 shadow-[0px_2px_8px_rgba(0,0,0,0.08)]"
        >
          {item.status === "adding" ? (
            <IconLoader2 className="size-4 shrink-0 animate-spin text-ds-mint-900" />
          ) : (
            <ReferenceIcon kind={item.kind} />
          )}
          <span className="truncate font-dm-sans text-xs text-ds-mint-900">
            {item.title}
          </span>
          {item.status === "referenced" && canRemove && (
            <button
              type="button"
              aria-label={t("Remove {{source}}", { source: item.title })}
              className="flex shrink-0 cursor-pointer items-center border-none bg-transparent p-0 text-[#7a6d65]"
              onClick={() => {
                iosGenerateHaptic(HapticEffect.SELECTION);
                references.onRemove(item.refId);
              }}
            >
              <IconX className="size-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The `@`/`+` mention autocomplete, shown above the composer while a trigger is
 * active. Tap a row to reference that source (the hook strips the token).
 * Renders nothing when there is no active trigger (`results === null`); an
 * active trigger that matched nothing renders a small "no match" line, so the
 * user knows the trigger was seen.
 */
export function ChatMentionDropdown({
  results,
  onSelect,
}: {
  results: ChatMentionResult[] | null;
  onSelect: (result: ChatMentionResult) => void;
}): React.ReactElement | null {
  const { t } = useTranslation();
  if (results === null) return null;

  return (
    <div className="mb-2 max-h-56 overflow-y-auto rounded-2xl bg-white p-1 shadow-[0px_2px_12px_rgba(0,0,0,0.12)]">
      {results.length === 0 ? (
        <p className="px-3 py-2 font-dm-sans text-xs text-ds-slate-500">
          {t("No matching playlist or article")}
        </p>
      ) : (
        results.map((result) => (
          <button
            key={`${result.kind}:${result.id}`}
            type="button"
            className="flex w-full cursor-pointer items-center gap-2 rounded-2xl border-none bg-transparent px-3 py-2 text-left hover:bg-ds-slate-50 active:bg-ds-slate-100"
            onClick={() => {
              iosGenerateHaptic(HapticEffect.SELECTION);
              onSelect(result);
            }}
          >
            <ReferenceIcon kind={result.kind} />
            <span className="truncate font-dm-sans text-sm text-ds-mint-900">
              {result.title}
            </span>
          </button>
        ))
      )}
    </div>
  );
}

/**
 * The conversation's referenced sources, shown IN the transcript above the
 * first turn — so a reference reads as part of the conversation and is still
 * there when reopened (the list is the server's, not session state). Tapping one
 * opens the playlist or the article. Shows only SETTLED references — an add in
 * flight belongs to the composer, where the spinner is.
 */
export function ChatReferenceList({
  references,
}: {
  references: ChatReferencesState;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const referenced = references.references.flatMap((item) =>
    item.status === "referenced" ? [item] : []
  );
  if (referenced.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <span className="font-dm-sans text-xs font-semibold text-[#7a6d65]">
        {t("Referenced sources")}
      </span>
      <div className="flex flex-wrap gap-2">
        {referenced.map((item) => {
          const href = referenceHref(item);
          const chipClassName =
            "flex max-w-full items-center gap-1.5 rounded-full bg-white px-3 py-1.5 no-underline shadow-[0px_2px_8px_rgba(0,0,0,0.08)]";
          const body = (
            <>
              <ReferenceIcon kind={item.kind} />
              <span className="truncate font-dm-sans text-xs text-ds-mint-900">
                {item.title}
              </span>
            </>
          );
          // The source is gone — the chip still names what grounded the answer,
          // but tapping it would land on a 404, so it is not a link.
          if (href === null)
            return (
              <div key={item.refId} className={chipClassName}>
                {body}
              </div>
            );

          return (
            <Link
              key={item.refId}
              to={href}
              className={`${chipClassName} cursor-pointer`}
              onClick={() => {
                iosGenerateHaptic(HapticEffect.SELECTION);
              }}
            >
              {body}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
